import { useMemo, useState } from 'react';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { HR } from 'foundations-components/transitional/components/HR';
import { Button } from 'foundations-components/transitional/components/Button';
import { SearchInput } from 'foundations-components/transitional/components/SearchInput';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { TicketListingsSortPopover } from './TicketListingsSortPopover';
import { FilterPopover } from './TicketListingsFilterPopover';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useIsMobile } from '../hooks/useBreakpoint';
import { DownloadIcon } from 'foundations-assets/icons/components/DownloadIcon';
import styled from 'foundations-theming-specialty/utils';
import { MOBILE_BREAKPOINT } from '../constants';
import { ViewToggleSwitch } from './ViewToggleSwitch';
import I18n from 'I18n';
import { View } from 'foundations-components/transitional/components/View';
import { useTicketListingsSortAndFilters } from '../hooks/useTicketListingsSortAndFilters';
import { usePortalData } from '../data/usePortalData';
import { useFormData } from '../data/useFormData';
import { useIsPreview } from '../hooks/useIsPreview';
import { usageTracker } from '../utils/usageTracker';
import { useTicketCreation } from './TicketCreationProvider';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FilterBarWrapper = styled.div.withConfig({
  displayName: "TicketListingsHeader__FilterBarWrapper"
})(["display:flex;flex-direction:column;align-items:stretch;justify-content:space-between;gap:", ";margin-block-end:", ";@media (min-width:", "px){flex-direction:row;align-items:center;gap:", ";}"], ({
  theme
}) => theme.space[200], ({
  theme
}) => theme.space[800], MOBILE_BREAKPOINT, ({
  theme
}) => theme.space[600]);
export function TicketListingsHeader({
  totalCount,
  csvExport
}) {
  const {
    view,
    setView,
    searchString,
    setSearchString
  } = useTicketListingsSortAndFilters();
  const {
    data
  } = usePortalData();
  const {
    ticketLabelOverride,
    formMenuText
  } = (data === null || data === void 0 ? void 0 : data.settings) || {};
  const isMobile = useIsMobile();
  const [searchTerm, setSearchTerm] = useState(searchString);
  const {
    data: formData
  } = useFormData();
  const hasForm = !!(formData !== null && formData !== void 0 && formData.formGuid);
  const isPreview = useIsPreview();
  const {
    open: openTicketCreationPanel
  } = useTicketCreation();
  const ticketListingsHeading = useMemo(() => {
    return ticketLabelOverride || I18n.text('customer-portal-app.ticketListings.heading.default');
  }, [ticketLabelOverride]);
  if (isMobile) {
    return /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      gap: "400",
      marginBlockEnd: "400",
      children: [/*#__PURE__*/_jsx(SearchInput, {
        "data-test-id": "search-input",
        clearable: false,
        inlineSize: "100%",
        placeholder: I18n.text('customer-portal-app.search.placeholder'),
        value: searchTerm,
        onChange: e => setSearchTerm(e.target.value),
        onKeyDown: e => {
          if (e.key === 'Enter') {
            usageTracker.track('Customer Portal App', 'Tickets Discovered', {
              action: 'tickets-searched',
              screen: 'ticket-listings'
            });
            setSearchString(searchTerm);
          }
        }
      }), /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        justifyContent: "space-between",
        children: [/*#__PURE__*/_jsx(Text, {
          "data-test-id": "ticket-listings-heading",
          typography: "body-500",
          color: "text-core-default",
          children: ticketListingsHeading
        }), /*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          gap: "400",
          children: [/*#__PURE__*/_jsx("div", {
            children: /*#__PURE__*/_jsx(LoadingButton, {
              marginInline: "0",
              size: "small",
              onClick: () => {
                if (isPreview) return;
                csvExport.doExport();
              },
              use: "secondary-alt",
              loading: csvExport.loading,
              failed: !!csvExport.error,
              "aria-label": I18n.text('customer-portal-app.ticketListings.export.button'),
              children: /*#__PURE__*/_jsx(DownloadIcon, {})
            })
          }), hasForm && /*#__PURE__*/_jsx("div", {
            children: /*#__PURE__*/_jsx(Button, {
              "data-test-id": "create-ticket-button",
              use: "primary",
              size: "small",
              onClick: () => {
                usageTracker.track('Customer Portal App', 'Tickets Manipulated', {
                  action: 'create-ticket-clicked',
                  screen: 'ticket-listings'
                });
                openTicketCreationPanel();
              },
              children: formMenuText
            })
          })]
        })]
      }), /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        justifyContent: "space-between",
        children: [/*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          children: [/*#__PURE__*/_jsx(FilterPopover, {}), /*#__PURE__*/_jsx(TicketListingsSortPopover, {})]
        }), /*#__PURE__*/_jsx(Text, {
          typography: "link-100",
          color: "text-core-subtle",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customer-portal-app.records",
            options: {
              count: totalCount || 0
            }
          })
        })]
      })]
    });
  }
  return /*#__PURE__*/_jsxs(View, {
    children: [/*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      justifyContent: "space-between",
      children: [/*#__PURE__*/_jsx(Text, {
        typography: "heading-500",
        "data-test-id": "ticket-listings-heading",
        color: "text-core-default",
        children: ticketListingsHeading
      }), hasForm && /*#__PURE__*/_jsx(Button, {
        use: "primary",
        "data-test-id": "create-ticket-button",
        onClick: () => {
          usageTracker.track('Customer Portal App', 'Tickets Manipulated', {
            action: 'create-ticket-clicked',
            screen: 'ticket-listings'
          });
          openTicketCreationPanel();
        },
        children: formMenuText
      })]
    }), /*#__PURE__*/_jsx(HR, {
      marginBlock: "800"
    }), /*#__PURE__*/_jsxs(FilterBarWrapper, {
      children: [/*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        gap: "400",
        children: [/*#__PURE__*/_jsx(SearchInput, {
          "data-test-id": "search-input",
          clearable: true,
          inlineSize: "370px",
          marginInlineEnd: "400",
          placeholder: I18n.text('customer-portal-app.search.placeholder'),
          value: searchTerm,
          onChange: e => {
            if (e.target.value === '') {
              setSearchString('');
              setSearchTerm('');
            } else {
              setSearchTerm(e.target.value);
            }
          },
          onKeyDown: e => {
            if (e.key === 'Enter') {
              usageTracker.track('Customer Portal App', 'Tickets Discovered', {
                action: 'tickets-searched',
                screen: 'ticket-listings'
              });
              setSearchString(searchTerm);
            }
          }
        }), /*#__PURE__*/_jsx(FilterPopover, {}), /*#__PURE__*/_jsx(TicketListingsSortPopover, {})]
      }), /*#__PURE__*/_jsxs(Flex, {
        gap: "600",
        alignItems: "center",
        children: [/*#__PURE__*/_jsx(Text, {
          typography: "link-100",
          color: "text-core-subtle",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            "data-test-id": "records-count",
            message: "customer-portal-app.records",
            options: {
              count: totalCount
            }
          })
        }), /*#__PURE__*/_jsx(ViewToggleSwitch, {
          view: view,
          onChange: setView
        }), /*#__PURE__*/_jsxs(LoadingButton, {
          "data-test-id": "csv-export-button",
          marginInline: "0",
          size: "small",
          onClick: () => {
            if (isPreview) return;
            usageTracker.track('Customer Portal App', 'Tickets Discovered', {
              action: 'tickets-exported',
              screen: 'ticket-listings'
            });
            csvExport.doExport();
          },
          use: "secondary-alt",
          loading: csvExport.loading,
          failed: !!csvExport.error,
          children: [/*#__PURE__*/_jsx(DownloadIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customer-portal-app.ticketListings.export.button"
          })]
        })]
      })]
    })]
  });
}